/**
 * Zod schema for the `Recipe` persistence shape + its slug-history row.
 *
 * Mirrors `../types/recipe.ts`. The TS type is the source of truth; this
 * schema is only the runtime validator. The `_Assert` line at the bottom
 * enforces bidirectional structural agreement with `Recipe`.
 *
 * Spec authority: `architecture.md §4.7` `CREATE TABLE recipes` +
 * `CREATE TABLE recipe_slug_history`.
 */

import { z } from "zod";
import type { Recipe, RecipeSlugHistoryEntry } from "../types/recipe.js";
import { HANDLE_SLUG_RE, UUID_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);
const slug = z.string().regex(HANDLE_SLUG_RE);
const timestamp = z.number().int().nonnegative();

export const RecipeSchema = z
  .object({
    uuid,
    slug,
    title: z.string(),
    description: z.string().nullable().optional(),
    emoji: z.string().nullable().optional(),
    prodCommitUuid: uuid.nullable().optional(),
    ratingSchemaJson: z.string(),
    visibility: z.enum(["private", "public"]),
    forkUpstreamRecipeUuid: uuid.nullable().optional(),
    forkUpstreamCommitUuid: uuid.nullable().optional(),
    createdAt: timestamp,
    updatedAt: timestamp,
  })
  .strict();

export const RecipeSlugHistoryEntrySchema = z
  .object({
    recipeUuid: uuid,
    slug,
    retiredAt: timestamp,
  })
  .strict();

// Bidirectional structural-agreement asserts — see implementer contract.
// Produces no runtime code; narrows to `never` on drift so `tsc` fails.
type _AssertRecipe =
  z.infer<typeof RecipeSchema> extends Recipe
    ? Recipe extends z.infer<typeof RecipeSchema>
      ? true
      : never
    : never;
type _AssertRecipeSlugHistory =
  z.infer<typeof RecipeSlugHistoryEntrySchema> extends RecipeSlugHistoryEntry
    ? RecipeSlugHistoryEntry extends z.infer<typeof RecipeSlugHistoryEntrySchema>
      ? true
      : never
    : never;
// Reference the aliases so "declared but never used" doesn't fire.
export type __RecipeSchemaAsserts = [_AssertRecipe, _AssertRecipeSlugHistory];
